import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Form, Formik, FormikProps } from 'formik';

import { PlayerPosition } from 'modules/teams/domain/enums/player-position';
import { Team, TeamInfo } from 'modules/teams/domain/interfaces/team';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import { SymbolicTeam } from 'modules/championship/domain/interfaces/symbolic-team';
import { upsertSymbolicTeam } from 'modules/championship/store/championship/actions';
import { championshipTeamPlayersNumberSelector } from 'modules/championship/store/championship/selectors';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import type { AppDispatch } from 'store/store';
import UDFormInput from 'ud-form/components/input';
import UDButton from 'ud-ui/components/button';
import { PlayersFilterValues } from 'modules/championship/domain/interfaces/players-filter-values';
import { SymbolicTeamFormValues } from 'modules/championship/domain/interfaces/symbolic-team-form-values';
import { buildEmptyPlayers } from 'modules/championship/domain/utils/build-empty-players';
import { getDefaultFormation } from 'modules/championship/domain/utils/get-default-formation';
import { PlayersFilter } from './players-filter';
import { PlayersList } from './players-list';
import { TeamFormation } from './team-formation/team-formation';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

const getSelectedPlayers = (values: SymbolicTeamFormValues) => {
  return values.players
    .filter((player) => !!player.user)
    .map((player) => ({
      rank: player.rank,
      userId: player.user!.id,
    }));
};

interface SymbolicTeamFormProps {
  allTeams: Team[];
  championshipId: number;
  symbolicTeam?: SymbolicTeam | null;
}

export const SymbolicTeamForm = ({ allTeams, championshipId, symbolicTeam = null }: SymbolicTeamFormProps) => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const dispatch = useDispatch<AppDispatch>();

  const teamPlayersNumber = useSelector(championshipTeamPlayersNumberSelector(championshipId));

  const [selectedRank, selectRank] = useState<number | null>(null);
  const [playersFilter, setPlayersFilter] = useState<PlayersFilterValues>({
    name: '',
    positions: [],
  });

  let players = symbolicTeam?.players ? [...symbolicTeam.players] : buildEmptyPlayers(teamPlayersNumber);
  if (players.length < teamPlayersNumber) {
    for (let i = players.length; i < teamPlayersNumber; i++) {
      players.push({ rank: i + 1 });
    }
  } else if (players.length > teamPlayersNumber) {
    players = players.filter((player) => player.rank <= teamPlayersNumber);
  }

  const initialValues: SymbolicTeamFormValues = {
    id: symbolicTeam?.id,
    championshipId,
    name: symbolicTeam?.name || '',
    formation: getDefaultFormation(teamPlayersNumber),
    players,
  };

  const allPlayers = useMemo(() => {
    return allTeams.reduce<TeamPlayer[]>((result, team) => {
      const teamPlayers = team.players.map((player) => ({
        ...player,
        team: {
          id: team.id,
          name: team.name,
          shortName: team.shortName,
          logo: team.logo,
        },
      }));

      return [...result, ...teamPlayers];
    }, []);
  }, [allTeams]);

  const filteredPlayers = useMemo(() => {
    let filtered = allPlayers;

    if (playersFilter.name.length > 2) {
      const query = playersFilter.name.toLowerCase();

      filtered = filtered.filter((p) => {
        const name = `${p.firstName.toLowerCase()} ${p.lastName.toLowerCase()}`;
        const reverseName = `${p.lastName.toLowerCase()} ${p.firstName.toLowerCase()}`;
        return name.includes(query) || reverseName.includes(query);
      });
    }

    if (playersFilter.positions.length) {
      filtered = filtered.filter((p) => playersFilter.positions.includes(p.position as PlayerPosition));
    }

    return filtered;
  }, [allPlayers, playersFilter]);

  const handlePlayersFilterChanged = (values: PlayersFilterValues) => {
    setPlayersFilter(values);
  };

  const handlePlayerSelect = useCallback(
    (formProps: FormikProps<SymbolicTeamFormValues>) => (player: TeamPlayer, team: TeamInfo) => {
      if (!selectedRank || !player) {
        return;
      }

      const newPlayers = formProps.values.players.map((item) => {
        if (item.rank === selectedRank) {
          return {
            ...item,
            teamLogo: team.logo?.url,
            user: {
              id: player.userId,
              teamId: team.id,
              firstName: player.firstName,
              lastName: player.lastName,
              avatar: player.avatar,
            },
          };
        }

        return item;
      });

      formProps.setFieldValue('players', newPlayers);
      selectRank(null);
    },
    [selectedRank],
  );

  // `async` — чтобы промис был и у ветки «не все игроки»: иначе Formik не снимет `isSubmitting` и кнопка застрянет.
  const handleSubmit = useCallback(
    async (values: SymbolicTeamFormValues) => {
      const isValid = values.name && values.players.every((player) => player.rank && player.user && player.user.id);
      if (!isValid) {
        toast.warning(t`Заполните всех игроков!`);
        return;
      }

      const data = {
        id: values.id,
        championshipId: values.championshipId,
        name: values.name,
        formation: values.formation,
        players: values.players.map((player) => ({
          rank: player.rank,
          userId: player.user!.id,
          teamId: player.user!.teamId,
        })),
      };

      // Без `.catch` провал был unhandled rejection и ничего не показывал.
      return dispatch(upsertSymbolicTeam(data))
        .unwrap()
        .then(() => {
          toast.success(t`Сборная успешно сохранена`);
        })
        .catch((error) => toastApiError(error, msg`Не удалось сохранить сборную`));
    },
    [dispatch, t, toastApiError],
  );

  return (
    <div>
      <div className="flex flex-wrap">
        <div className="mb-3 w-1/2"></div>
      </div>

      <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={handleSubmit}>
        {(formProps) => (
          <Form onSubmit={formProps.handleSubmit}>
            <div className="flex flex-wrap">
              <div className="w-1/2">
                <div className="py-4">
                  <PlayersFilter className="mb-6" onChange={handlePlayersFilterChanged} />
                  <PlayersList
                    formation={formProps.values.formation}
                    allPlayers={filteredPlayers}
                    selectedPlayers={getSelectedPlayers(formProps.values)}
                    onPlayerSelect={handlePlayerSelect(formProps)}
                  />
                </div>
              </div>
              <div className="w-1/2">
                <div className="mb-8 px-8 py-4">
                  <UDFormInput name="name" label={t`Название`} placeholder={t`Название сборной`} />
                </div>

                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                  <div className="pt-4">
                    <div className="p-8">
                      <TeamFormation
                        formation={formProps.values.formation}
                        players={formProps.values.players}
                        selectedRank={selectedRank}
                        onRankSelect={selectRank}
                      />
                    </div>
                  </div>

                  <div className="p-8">
                    <UDButton variant="primary" type="submit" className="mt-3 mb-3 w-full">
                      <Trans>Сохранить</Trans>
                    </UDButton>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
